import React, { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { Pencil, Plus } from 'lucide-react';

import {
	useDeactivateUserMutation,
	useGetTeamMembersQuery,
	useInviteTeamMemberMutation,
	useReactivateUserMutation,
	useUpdateUserMutation,
} from '../api/teams';
import {
	Alert,
	Button,
	FilterDropdown,
	Input,
	Modal,
	ModalBody,
	ModalFooter,
	PageContent,
	PageHeader,
	SearchInput,
	SkeletonTable,
	Textarea,
	Typography,
} from '../components/common';
import ConfirmationModal from '../components/ConfirmationModal';
import RolePermissionsCard from '../components/RolePermissionsCard';
import { useToast } from '../context/ToastContext';
import { useFeature } from '../hooks';
import { useOnboarding } from '../hooks/useOnboarding';
import { useAuth } from '../providers/AppAuthProvider';
import { useTenant } from '../providers/TenantProvider';
import { hasAnyRole } from '../utils/utils';

import type { TeamMember } from '../api/teams';
import type { FilterOption } from '../components/common';

// v9 table, `.btn-secondary.btn-sm` and `table.compact` styles.
const V9_TABLE =
	'min-w-full border-collapse [&_tbody_tr:last-child_td]:border-b-0';
const V9_TH =
	'bg-gray-50 px-4 py-2.5 text-left text-xs font-medium uppercase tracking-[.05em] text-gray-500 border-b border-gray-200 whitespace-nowrap';
const V9_TD =
	'px-4 py-2.5 text-sm text-gray-900 border-b border-gray-200 whitespace-nowrap align-middle';
const V9_BTN_SM =
	'inline-flex items-center gap-2 px-3 py-1 text-xs font-medium leading-[18px] rounded-md border border-gray-300 bg-white text-primary-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed';

const TeamPage: React.FC = () => {
	// ROC-102: tenants with the `roles` flag use Sales / Pricing team / Manager / Admin.
	const rolesOn = useFeature('roles');
	const defaultRole = rolesOn ? 'sales' : 'user';
	const [allMembers, setAllMembers] = useState<TeamMember[]>([]);
	const [filteredMembers, setFilteredMembers] = useState<TeamMember[]>([]);
	const [isAdmin, setIsAdmin] = useState(false);
	const [showInviteModal, setShowInviteModal] = useState(false);
	const [inviteEmails, setInviteEmails] = useState('');
	const [selectedRole, setSelectedRole] = useState(defaultRole);

	// Edit user states
	const [editingUser, setEditingUser] = useState<TeamMember | null>(null);
	const [showEditModal, setShowEditModal] = useState(false);
	const [editName, setEditName] = useState('');
	const [editRole, setEditRole] = useState('');

	// Confirmation modal states
	const [showConfirmationModal, setShowConfirmationModal] = useState(false);
	const [modalAction, setModalAction] = useState<'deactivate' | 'reactivate'>(
		'deactivate',
	);
	const [userForAction, setUserForAction] = useState<TeamMember | null>(null);

	// Filter and search states
	const [selectedStatuses, setSelectedStatuses] = useState<string[]>(
		rolesOn ? ['active', 'invited'] : ['active'],
	); // Default to showing active users
	const [searchQuery, setSearchQuery] = useState<string>('');

	// Pagination state
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [perPage, setPerPage] = useState<number>(10);

	// Debounced search state
	const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');

	const { user } = useAuth();
	const { showToast } = useToast();
	const navigate = useNavigate();
	const { completeStepById, isStepCompleted, startedAt } = useOnboarding();
	const { tenant } = useTenant();

	// Consolidated permission checks (single useMemo to avoid minification variable collisions)
	const permissionChecks = React.useMemo(() => {
		// Check if current user can edit enterprise_admin users
		// Only super_admin and tenant_admin can edit enterprise_admin
		const hasTenantAdminAccess = hasAnyRole(user?.roles, [
			'super_admin',
			'tenant_admin',
		]);

		// Check if we're on the main tenant (public schema) - no tenant means main tenant
		const onMainTenant =
			!tenant || tenant.schema_name === 'public' || !tenant.schema_name;

		return {
			canEditEnterpriseAdmin: hasTenantAdminAccess,
			isMainTenant: onMainTenant,
			// Only super_admin and tenant_admin on main tenant can assign super_admin/tenant_admin roles
			canAssignTenantRoles: onMainTenant && hasTenantAdminAccess,
		};
	}, [user?.roles, tenant]);

	const { canEditEnterpriseAdmin, isMainTenant, canAssignTenantRoles } =
		permissionChecks;

	// Role definitions with descriptions for user guidance
	const roleDefinitions: Record<
		string,
		{ label: string; description: string }
	> = {
		user: rolesOn
			? {
					label: 'Sales',
					description:
						'Sees assigned merchants and edits quotes inside the rep range.',
				}
			: {
					label: 'User',
					description:
						'Basic access to view and upload statements. Cannot manage team members.',
				},
		sales: {
			label: 'Sales',
			description:
				'Sees assigned merchants and edits quotes inside the rep range.',
		},
		pricing: {
			label: 'Pricing team',
			description:
				'Uploads and analyzes every statement, edits inside the template range and manages templates.',
		},
		manager: {
			label: 'Manager',
			description:
				'Sees every statement, approves exceptions and sees analytics.',
		},
		enterprise_analyst: {
			label: 'Organization Analyst',
			description:
				'Read-only access to view statements and reports. Cannot make changes or manage team.',
		},
		admin: {
			label: 'Admin',
			description:
				'Can manage team members, view all statements, and configure organization settings.',
		},
		enterprise_admin: {
			label: 'Owner',
			description:
				'Full control over the organization including billing, team management, and all settings.',
		},
		tenant_analyst: {
			label: 'Tenant Analyst',
			description:
				'Read-only access across all organizations in the tenant. For reporting and auditing purposes.',
		},
		tenant_admin: {
			label: 'Tenant Admin',
			description:
				'Can manage all organizations and users within the tenant. Full administrative access.',
		},
		super_admin: {
			label: 'Super Admin',
			description:
				'Highest level access. Can manage tenants, all organizations, and system-wide settings.',
		},
	};

	// Get available role options based on current user's permissions and tenant context
	const getRoleOptions = React.useMemo(() => {
		const baseRoleValues = rolesOn
			? ['sales', 'pricing', 'manager', 'admin', 'enterprise_admin']
			: ['user', 'enterprise_analyst', 'admin', 'enterprise_admin'];

		// Only show tenant-level roles if on main tenant and user has permission
		const roleValues = canAssignTenantRoles
			? [...baseRoleValues, 'tenant_analyst', 'tenant_admin', 'super_admin']
			: baseRoleValues;

		return roleValues.map((value) => ({
			value,
			label: roleDefinitions[value]?.label || value,
			description: roleDefinitions[value]?.description || '',
		}));
	}, [canAssignTenantRoles, rolesOn]);

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchQuery(searchQuery);
		}, 300); // 300ms delay

		return () => clearTimeout(timer);
	}, [searchQuery]);

	// API hooks
	const {
		data: teamData,
		isLoading,
		isFetching,
		refetch,
	} = useGetTeamMembersQuery({
		status: selectedStatuses.length === 0 ? 'all' : selectedStatuses.join(','),
		page: currentPage,
		per_page: perPage,
		search: debouncedSearchQuery.trim() || undefined,
	});

	const [inviteTeamMember, { isLoading: inviting }] =
		useInviteTeamMemberMutation();
	const [updateUser, { isLoading: updating }] = useUpdateUserMutation();
	const [deactivateUser, { isLoading: deactivating }] =
		useDeactivateUserMutation();
	const [reactivateUser] = useReactivateUserMutation();

	// Status filter options
	const statusOptions: FilterOption[] = [
		{ id: 'active', label: 'Active', value: 'active' },
		{ id: 'invited', label: 'Invited', value: 'invited' },
		{
			id: 'pending_verification',
			label: 'Pending Verification',
			value: 'pending_verification',
		},
		{ id: 'expired', label: 'Expired', value: 'expired' },
		{ id: 'cancelled', label: 'Cancelled', value: 'cancelled' },
		{ id: 'inactive', label: 'Inactive', value: 'inactive' },
	];

	useEffect(() => {
		if (!user) return;

		// Check if user is super_admin first (doesn't require teamData)
		const isSuperAdmin =
			Array.isArray(user?.roles) && user?.roles.includes('super_admin');

		// If super_admin, grant access immediately
		if (isSuperAdmin) {
			setIsAdmin(true);
			return;
		}

		// For non-super_admin users, wait for teamData
		if (!teamData) return;

		// Check if user is admin using the API response
		const userIsAdmin = teamData.isTeamAdmin || false;
		const hasAccess = userIsAdmin;
		setIsAdmin(hasAccess);

		if (!hasAccess) {
			showToast(
				'Access Denied',
				'You do not have permission to access this page',
				'error',
			);
			navigate('/');
			return;
		}
	}, [user, teamData, navigate, showToast]);

	// Update allMembers when team data changes
	useEffect(() => {
		if (teamData?.members) {
			setAllMembers(teamData.members);
		}
	}, [teamData]);

	useEffect(() => {
		setCurrentPage(1);
		refetch();
	}, [selectedStatuses, refetch]);

	useEffect(() => {
		setCurrentPage(1);
		refetch();
	}, [debouncedSearchQuery, refetch]);

	useEffect(() => {
		refetch();
	}, [currentPage, perPage, refetch]);

	// Filter and search effect
	useEffect(() => {
		let filtered = allMembers;

		// Apply status filter (backend already handles this, but we can add additional filtering)
		if (selectedStatuses.length > 0) {
			filtered = filtered.filter((member) =>
				selectedStatuses.includes(member.status),
			);
		}

		setFilteredMembers(filtered);
	}, [allMembers, selectedStatuses]);

	const handleInvite = async () => {
		if (!inviteEmails.trim()) {
			showToast(
				'Validation Error',
				'Please enter at least one email address',
				'error',
			);
			return;
		}

		if (!selectedRole) {
			showToast('Validation Error', 'Please select a role', 'error');
			return;
		}

		const emails = inviteEmails
			.split(',')
			.map((email) => email.trim())
			.filter(Boolean);

		try {
			// Send invites concurrently
			const invitePromises = emails.map(async (email) => {
				const name = email.split('@')[0]; // Use email prefix as default name
				return inviteTeamMember({ email, name, role: selectedRole }).unwrap();
			});

			await Promise.all(invitePromises);

			showToast(
				'Success',
				`Successfully sent ${emails.length} invitation(s) with role: ${selectedRole}`,
				'success',
			);

			// Auto-complete the team step when invitation is sent
			if (startedAt && !isStepCompleted(4)) {
				completeStepById(4);
				navigate('/onboarding');
			}

			setShowInviteModal(false);
			setInviteEmails('');
			setSelectedRole(defaultRole);

			// Refresh team data
			refetch();
		} catch (error: any) {
			console.error('Error sending invites:', error);

			if (error?.data?.message) {
				showToast('Validation Error', error.data.message, 'error');
			} else {
				showToast('Error', 'Failed to send some invitations', 'error');
			}
		}
	};

	const handleEditUser = (member: TeamMember) => {
		// Prevent editing current user (they should use profile settings)
		if (member.id === user?.id) {
			showToast(
				'Error',
				'Please use your profile settings to edit your own information',
				'error',
			);
			return;
		}

		// Check if current user can edit enterprise_admin users
		if (member.role === 'enterprise_admin' && !canEditEnterpriseAdmin) {
			showToast('Error', 'Cannot edit Owner users', 'error');
			return;
		}

		setEditingUser(member);
		setEditName(member.name || member.email.split('@')[0]);
		setEditRole(member.role);
		setShowEditModal(true);
	};

	const handleUpdateUser = async () => {
		if (!editingUser) return;

		// Check if current user can edit enterprise_admin users
		if (editingUser.role === 'enterprise_admin' && !canEditEnterpriseAdmin) {
			showToast('Error', 'Cannot edit Owner users', 'error');
			return;
		}

		// Prevent current user from changing their role
		if (editingUser.id === user?.id && editRole !== editingUser.role) {
			showToast('Error', 'You cannot change your own role', 'error');
			return;
		}

		try {
			const updateData: any = {};
			if (editName !== editingUser.name) updateData.name = editName;
			if (editRole !== editingUser.role) updateData.role = editRole;

			if (Object.keys(updateData).length === 0) {
				setShowEditModal(false);
				return;
			}

			await updateUser({ userId: editingUser.id, data: updateData }).unwrap();
			showToast('Success', 'User updated successfully', 'success');
			setShowEditModal(false);
			setEditingUser(null);
			refetch();
		} catch (error) {
			console.error('Error updating user:', error);
			showToast('Error', 'Failed to update user', 'error');
		}
	};

	const handleDeactivateUser = async (memberId: string) => {
		// Prevent self-deactivation
		if (memberId === user?.id) {
			showToast(
				'Error',
				'You cannot deactivate yourself from the team',
				'error',
			);
			return;
		}

		// Find the member to check their role
		const memberToDelete = allMembers.find((m) => m.id === memberId);

		// Check if current user can deactivate enterprise_admin users
		// Only super_admin and tenant_admin can deactivate enterprise_admin
		if (
			memberToDelete?.role === 'enterprise_admin' &&
			!canEditEnterpriseAdmin
		) {
			showToast('Error', 'Cannot deactivate Owner users', 'error');
			return;
		}

		// Show confirmation modal
		if (memberToDelete) {
			setUserForAction(memberToDelete);
			setModalAction('deactivate');
			setShowConfirmationModal(true);
		}
	};

	const confirmDeactivateUser = async () => {
		if (!userForAction) return;

		try {
			await deactivateUser(userForAction.id).unwrap();
			showToast('Success', 'User deactivated successfully', 'success');
			setShowConfirmationModal(false);
			setUserForAction(null);
			refetch();
		} catch (error) {
			console.error('Error deactivating user:', error);
			showToast('Error', 'Failed to deactivate user', 'error');
		}
	};

	const handleReactivateUser = async (memberId: string) => {
		const memberToReactivate = allMembers.find((m) => m.id === memberId);
		if (memberToReactivate) {
			setUserForAction(memberToReactivate);
			setModalAction('reactivate');
			setShowConfirmationModal(true);
		}
	};

	const confirmReactivateUser = async () => {
		if (!userForAction) return;

		try {
			await reactivateUser(userForAction.id).unwrap();
			showToast('Success', 'User reactivated successfully', 'success');
			setShowConfirmationModal(false);
			setUserForAction(null);
			refetch();
		} catch (error) {
			console.error('Error reactivating user:', error);
			showToast('Error', 'Failed to reactivate user', 'error');
		}
	};

	// v9 `.pill`: Active is green, Invited is sand, everything else neutral.
	const getStatusBadge = (status: string) => {
		const label =
			statusOptions.find((o) => o.value === status)?.label ??
			status.charAt(0).toUpperCase() + status.slice(1);
		const tone =
			status === 'active'
				? 'bg-green-100 text-green-800 border-green-200'
				: status === 'invited'
					? 'bg-sand-100 text-sand-900 border-sand-300'
					: 'bg-gray-100 text-gray-600 border-gray-200';
		return (
			<span
				className={`inline-flex items-center px-2 py-1 text-xs font-medium leading-[1.3] rounded-full border whitespace-nowrap ${tone}`}
			>
				{label}
			</span>
		);
	};

	const handleResendInvite = async (member: TeamMember) => {
		try {
			await inviteTeamMember({
				email: member.email,
				name: member.name,
				role: member.role,
			}).unwrap();
			showToast('Invite resent', `Sent again to ${member.email}.`, 'success');
		} catch {
			showToast('Error', 'Failed to resend the invitation', 'error');
		}
	};

	if (!isAdmin) {
		return null;
	}

	return (
		<div className="flex flex-1 flex-col">
			<PageHeader
				title="Team Management"
				variant="title"
				actions={
					<Button
						onClick={() => setShowInviteModal(true)}
						variant="primary"
						className="flex items-center gap-2"
						icon={Plus}
					>
						Add Member
					</Button>
				}
			/>

			<PageContent>
				{/* Team Members Section */}
				<div className="w-full overflow-hidden bg-white border border-gray-200 rounded-lg">
					<div className="flex items-center justify-between gap-4 px-6 pt-6">
						<div className="text-lg font-normal text-gray-900">
							All Team Members ({filteredMembers.length})
						</div>
						<div className="flex items-center gap-3">
							{/* Search */}
							<div className="relative w-64">
								<SearchInput
									placeholder="Search"
									value={searchQuery}
									onChange={(value) => {
										setSearchQuery(value);
									}}
								/>
								{isFetching && searchQuery && (
									<div className="absolute inset-y-0 right-3 flex items-center">
										<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
									</div>
								)}
							</div>

							{/* Filter Button */}
							<FilterDropdown
								options={statusOptions}
								selectedValues={selectedStatuses}
								onSelectionChange={setSelectedStatuses}
								placeholder="Filter"
								title="Filter by status"
								variant="outline"
								size="md"
							/>
						</div>
					</div>

					{isLoading || isFetching ? (
						<div className="w-full mt-6">
							<SkeletonTable rows={10} columns={5} showHeader={true} />
						</div>
					) : (
						<div className="overflow-x-auto mt-6">
							<table className={V9_TABLE}>
								<thead>
									<tr>
										<th className={V9_TH}>Name</th>
										<th className={V9_TH}>Email</th>
										<th className={V9_TH}>Role</th>
										<th className={V9_TH}>Status</th>
										<th className={V9_TH}>Actions</th>
									</tr>
								</thead>
								<tbody>
									{filteredMembers.map((member) => (
										<tr key={member.id}>
											<td className={`${V9_TD} text-primary-700`}>
												{member.name || member.email.split('@')[0]}
												{member.id === user?.id && (
													<span className="ml-1 text-xs text-gray-500">
														(You)
													</span>
												)}
											</td>
											<td className={`${V9_TD} text-gray-500`}>
												{member.email}
											</td>
											<td className={`${V9_TD} text-primary-700`}>
												{roleDefinitions[member.role]?.label ?? member.role}
											</td>
											<td className={V9_TD}>{getStatusBadge(member.status)}</td>
											<td className={V9_TD}>
												<span className="flex items-center gap-2">
													{member.status === 'inactive' ? (
														<button
															type="button"
															onClick={() => handleReactivateUser(member.id)}
															className={V9_BTN_SM}
															title="Reactivate user"
														>
															Reactivate
														</button>
													) : (
														<>
															<button
																type="button"
																onClick={() => handleEditUser(member)}
																className={V9_BTN_SM}
															>
																<Pencil className="h-3 w-3" />
																Edit
															</button>
															{member.status === 'invited' ? (
																<button
																	type="button"
																	onClick={() => handleResendInvite(member)}
																	disabled={inviting}
																	className={V9_BTN_SM}
																>
																	Resend
																</button>
															) : (
																<button
																	type="button"
																	onClick={() => handleDeactivateUser(member.id)}
																	disabled={deactivating}
																	className={`${V9_BTN_SM} !text-red-700`}
																	title="Deactivate user (reversible - data preserved)"
																>
																	Deactivate
																</button>
															)}
														</>
													)}
												</span>
											</td>
										</tr>
									))}
								</tbody>
							</table>

							{filteredMembers.length === 0 && (
								<div className="text-center py-8">
									<p className="text-gray-500">
										No users found matching your criteria.
									</p>
								</div>
							)}
						</div>
					)}
					{teamData?.pagination && teamData.pagination.pages > 1 && (
						<div className="px-6 py-4 border-t border-gray-200">
							<div className="flex items-center justify-between">
								<div className="flex items-center space-x-2">
									<Typography variant="body2" color="muted">
										Showing{' '}
										{(teamData.pagination.page - 1) *
											teamData.pagination.per_page +
											1}{' '}
										to{' '}
										{Math.min(
											teamData.pagination.page * teamData.pagination.per_page,
											teamData.pagination.total,
										)}{' '}
										of {teamData.pagination.total} results
									</Typography>
								</div>
								<div className="flex items-center space-x-2">
									<Button
										onClick={() => setCurrentPage(currentPage - 1)}
										disabled={currentPage === 1}
										variant="secondary"
										size="sm"
									>
										Previous
									</Button>
									<Typography variant="body2" color="muted">
										Page {currentPage} of {teamData.pagination.pages}
									</Typography>
									<Button
										onClick={() => setCurrentPage(currentPage + 1)}
										disabled={currentPage === teamData.pagination.pages}
										variant="secondary"
										size="sm"
									>
										Next
									</Button>
								</div>
							</div>
						</div>
					)}
				</div>

				{rolesOn && <RolePermissionsCard />}

				{/* Invite Modal */}
				<Modal
					isOpen={showInviteModal}
					onClose={() => setShowInviteModal(false)}
					title="Invite Team Members"
					size="lg"
				>
					<ModalBody>
						<Typography variant="body2" color="muted" className="mb-4">
							Enter email addresses and select a role for the new team members.
						</Typography>
						<div className="space-y-4">
							<Textarea
								label="Email Addresses (comma-separated)"
								value={inviteEmails}
								onChange={(e) => setInviteEmails(e.target.value)}
								placeholder="email1@example.com, email2@example.com"
								rows={3}
							/>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-2">
									Role
								</label>
								<div className="space-y-2">
									{getRoleOptions.map((role) => (
										<label
											key={role.value}
											className={`flex items-start p-3 border rounded-lg cursor-pointer transition-colors ${
												selectedRole === role.value
													? 'border-purple-500 bg-purple-50'
													: 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
											}`}
										>
											<input
												type="radio"
												name="invite-role"
												value={role.value}
												checked={selectedRole === role.value}
												onChange={(e) => setSelectedRole(e.target.value)}
												className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
											/>
											<div className="ml-3">
												<span className="block text-sm font-medium text-gray-900">
													{role.label}
												</span>
												<span className="block text-xs text-gray-500 mt-0.5">
													{role.description}
												</span>
											</div>
										</label>
									))}
								</div>
							</div>
						</div>
					</ModalBody>
					<ModalFooter>
						<Button
							onClick={() => setShowInviteModal(false)}
							variant="secondary"
						>
							Cancel
						</Button>
						<Button
							onClick={handleInvite}
							disabled={inviting || !inviteEmails.trim() || !selectedRole}
							variant="primary"
							loading={inviting}
						>
							Send Invites
						</Button>
					</ModalFooter>
				</Modal>

				{/* Edit User Modal */}
				<Modal
					isOpen={showEditModal && !!editingUser}
					onClose={() => {
						setShowEditModal(false);
						setEditingUser(null);
					}}
					title="Edit Team Member"
					size="lg"
				>
					<ModalBody>
						<Typography variant="body2" color="muted" className="mb-4">
							Update the team member's information.
						</Typography>

						{editingUser?.role === 'enterprise_admin' &&
							!canEditEnterpriseAdmin && (
								<Alert variant="warning" className="mb-4">
									Owner users have restricted editing capabilities.
								</Alert>
							)}

						{editingUser?.id === user?.id && (
							<Alert variant="info" className="mb-4">
								You can edit your own profile information.
							</Alert>
						)}

						<div className="space-y-4">
							<Input
								label="Name"
								type="text"
								value={editName}
								onChange={(e) => setEditName(e.target.value)}
								placeholder="Enter name"
							/>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-2">
									Role
								</label>
								{editingUser?.id === user?.id ||
								(editingUser?.role === 'enterprise_admin' &&
									!canEditEnterpriseAdmin) ? (
									// Show disabled select for restricted cases
									<>
										<select
											value={editRole}
											disabled
											className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
										>
											<option value={editRole}>
												{roleDefinitions[editRole]?.label || editRole}
											</option>
										</select>
										<Typography
											variant="caption"
											color="muted"
											className="mt-1"
										>
											{editingUser?.id === user?.id
												? 'You cannot change your own role'
												: 'Owner role cannot be changed'}
										</Typography>
									</>
								) : (
									// Show radio buttons with descriptions for editable cases
									<div className="space-y-2 max-h-64 overflow-y-auto">
										{getRoleOptions.map((role) => (
											<label
												key={role.value}
												className={`flex items-start p-3 border rounded-lg cursor-pointer transition-colors ${
													editRole === role.value
														? 'border-purple-500 bg-purple-50'
														: 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
												}`}
											>
												<input
													type="radio"
													name="edit-role"
													value={role.value}
													checked={editRole === role.value}
													onChange={(e) => setEditRole(e.target.value)}
													className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
												/>
												<div className="ml-3">
													<span className="block text-sm font-medium text-gray-900">
														{role.label}
													</span>
													<span className="block text-xs text-gray-500 mt-0.5">
														{role.description}
													</span>
												</div>
											</label>
										))}
									</div>
								)}
							</div>
						</div>
					</ModalBody>
					<ModalFooter>
						<Button
							onClick={() => {
								setShowEditModal(false);
								setEditingUser(null);
							}}
							variant="secondary"
						>
							Cancel
						</Button>
						<Button
							onClick={handleUpdateUser}
							disabled={updating}
							variant="primary"
							loading={updating}
						>
							Update User
						</Button>
					</ModalFooter>
				</Modal>

				{/* Unified Confirmation Modal */}
				<ConfirmationModal
					isOpen={showConfirmationModal}
					user={userForAction}
					actionType={modalAction}
					onClose={() => {
						setShowConfirmationModal(false);
						setUserForAction(null);
					}}
					onConfirm={
						modalAction === 'deactivate'
							? confirmDeactivateUser
							: confirmReactivateUser
					}
					isLoading={modalAction === 'deactivate' ? deactivating : false}
				/>
			</PageContent>
		</div>
	);
};

export default TeamPage;
